/* eslint-disable no-console */
import { AUTH_TENANT_KEY, ACCOUNTS_KEY, FORCELOGIN_KEY } from '../constants';
import { clearAll } from '../util/clear-all';
import { Storage } from '../util/storage';

/**
 * Registers tenant name in local storage
 * @param {string} name
 */
export function registerTenantName(name: string): void {
    const tenantNames = JSON.parse(Storage.getItem(ACCOUNTS_KEY)) || [];
    if (!tenantNames.includes(name)) {
        tenantNames.push(name);
        Storage.setItem(ACCOUNTS_KEY, JSON.stringify(tenantNames));
    }
}

/**
 * Returns tenant account or empty object from localStorage
 * @param {string} name Optional tenant name, default name session storage
 * @returns {Record<string, any> | {}}
 */
export const getTenant = (name?: string): Record<string, any> => {
    try {
        const tenantName = name || getTenantName();
        return JSON.parse(Storage.getItem(tenantName)) || {};
    } catch (error) {
        return {};
    }
};

/**
 * Sets tenant in local storage
 * @param {string} name
 * @param {Record<string, any>} value
 */
export const setTenant = (name: string, value: Record<string, any>): void => {
    try {
        Storage.setItem(name, JSON.stringify(value));
    } catch (error) {
        console.warn('account-manager error: setTenant method');
    }
};

/**
 * Returns tenant name from session storage or null
 * @returns {string}
 */
export const getTenantName = (): string | null => {
    try {
        const pathName = window.location.pathname.substring(1);
        const tenantName = pathName.substring(0, pathName.indexOf('/'));
        return window.sessionStorage.getItem(AUTH_TENANT_KEY) || tenantName;
    } catch (error) {
        console.warn('account-manager error: getTenantName method');
    }
};

/**
 * Sets tenant name in session storage
 * @param {string} name
 */
export const setTenantName = (name: string): void => {
    try {
        window.sessionStorage.setItem(AUTH_TENANT_KEY, name);
    } catch (error) {
        console.warn('account-manager error: setTenanName method');
    }
};

/**
 * Removes tenant name in session storage
 */
export const removeTenantName = (): void => {
    try {
        window.sessionStorage.removeItem(AUTH_TENANT_KEY);
    } catch (error) {
        console.warn('account-manager error: removeTenantName method');
    }
};

/**
 * Returns tenants from local storage
 * @returns {Record<string, any>[]}
 */
export function getTenantList(): Record<string, any>[] {
    const tenants = JSON.parse(Storage.getItem(ACCOUNTS_KEY)) || [];
    return tenants.reduce((acc: any, value: string) => {
        const details = JSON.parse(Storage.getItem(value));
        if (details) {
            acc.push(details);
        }
        return acc;
    }, []);
}

/**
 * Clears tenant from local storage
 * @param {string} name Optional tenant name, default session storage name
 */
export const removeTenant = (name?: string): void => {
    try {
        const tenantName = name || getTenantName();
        Storage.removeItem(tenantName);
    } catch (error) {
        console.warn('account-manager error: clearAccount method');
    }
};

/**
 * Gets a value from the tenant
 * @param {string} key
 * @param {string} name Optional tenant name, default session storage name
 * @returns {any | null}
 */
export const getItem = (key: string, name?: string): any | null => {
    try {
        const tenant = getTenant(name);
        return tenant[key];
    } catch (error) {
        return null;
    }
};

/**
 * Sets an item [key, value] in the tenant
 * @param {string} key
 * @param {any} value
 * @param {string} name Optional tenant name, default session storage name
 */
export const setItem = (key: string, value: any, name?: string): void => {
    try {
        const tenantName = name || getTenantName();
        if (tenantName) {
            const tenant = getTenant(tenantName);
            tenant[key] = value;
            Storage.setItem(tenantName, JSON.stringify(tenant));
        }
    } catch (error) {
        console.warn(`account-manager error: setItem [${key}, ${value}]`);
    }
};

/**
 * Removes an item from tenant
 * @param {string} key
 * @param {string} name Optional tenant name, default session storage name
 */
export const removeItem = (key: string, name?: string): void => {
    try {
        const tenantName = name || getTenantName();
        if (tenantName) {
            const tenant = getTenant(tenantName);
            delete tenant[key];
            Storage.setItem(tenantName, JSON.stringify(tenant));
        }
    } catch (error) {
        console.log(`account-manager error: removeItem [${key}]`);
    }
};

/**
 * Clears browser cookies, local and session storage
 */
export const clearMsalSession = (): void => {
    const tenants = getTenantList();
    const tenantNames = [];
    clearAll();
    tenants.forEach((tenant) => {
        tenantNames.push(tenant.name);
        Storage.setItem(tenant.name, JSON.stringify(tenant));
    });
    Storage.setItem(ACCOUNTS_KEY, JSON.stringify(tenantNames));
};

/**
 * Removes forcelogin flag from session storage
 */
export const removeForceLogin = (): void => {
    try {
        window.sessionStorage.removeItem(FORCELOGIN_KEY);
    } catch (error) {
        console.warn('account-manager error: removeForceLogin method');
    }
};
